import { levenshteinDistanceWithin } from './stringMatch.js'
import { SEARCH_RESULT_TYPES, WTY } from '../constants/generalConstants.js'

const normalize = value =>
  value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ł/g, 'l')
    .replace(/[^\p{L}\p{N}\p{M}]+/gu, ' ')
    .trim()

export const detectServiceWarrantyIntent = (keyword, language, config) => {
  const termsByLanguage = config?.termsByLanguage
  if (config?.enabled !== true || !termsByLanguage || typeof termsByLanguage !== 'object') return null
  const languageCode = language?.toLowerCase().split(/[_-]/)[0]
  const terms = termsByLanguage[languageCode]
  if (!Array.isArray(terms) || terms.some(term => typeof term !== 'string' || !term.trim())) {
    return null
  }

  const query = normalize(keyword)
  if (!query) return null
  const variants = terms.map(term => ({ term, normalized: normalize(term) })).filter(variant => variant.normalized)
  const exact = variants.find(({ normalized }) => ` ${query} `.includes(` ${normalized} `))
  if (exact) return { term: exact.term, language: languageCode, method: 'exact', distance: 0 }

  const queryWords = query.split(' ')
  for (const variant of variants) {
    const termWords = variant.normalized.split(' ')
    for (let start = 0; start <= queryWords.length - termWords.length; start++) {
      let totalDistance = 0
      const maxDistance = variant.normalized.replace(/ /g, '').length >= 9 ? 2 : 1
      for (let index = 0; index < termWords.length; index++) {
        const word = termWords[index]
        const wordMax = word.length <= 4 ? 0 : word.length <= 8 ? 1 : 2
        const distance = levenshteinDistanceWithin(queryWords[start + index], word, wordMax, { transpositions: true })
        if (distance === null) {
          totalDistance = Infinity
          break
        }
        totalDistance += distance
      }
      if (totalDistance <= maxDistance) {
        return { term: variant.term, language: languageCode, method: 'fuzzy', distance: totalDistance }
      }
    }
  }
  return null
}

export const promoteServiceWarrantyResult = (results, recommendation) => {
  if (!recommendation) return results
  const isWarranty = item => item.type === SEARCH_RESULT_TYPES.PUBLICATION && item.publicationType === WTY
  const variants = [
    recommendation,
    ...results.filter(item => isWarranty(item) && item.promoted && item.product?.year === recommendation.product?.year)
  ]
  const pdf = variants.find(item => item.isWebViewPdf)
  const html = variants.find(item => item.isHTML)
  const matches = [...variants, ...results.filter(item => isWarranty(item) && !item.promoted)]
  const maxMatches = predicate =>
    Math.max(0, ...matches.filter(predicate).map(item => item.matchCount ?? item.topics?.length ?? 0))
  const pdfMatches = maxMatches(item => item.isWebViewPdf)
  const htmlMatches = maxMatches(item => item.isHTML && !item.isWebViewPdf)
  const rows = []
  if (pdf)
    rows.push({
      ...pdf,
      id: 'service-warranty-pdf',
      isHTML: false,
      isWebViewPdf: true,
      ...(pdfMatches > 0 && { matchCount: pdfMatches })
    })
  if (html)
    rows.push({
      ...html,
      id: 'service-warranty-html',
      isHTML: true,
      isWebViewPdf: false,
      ...(htmlMatches > 0 && { matchCount: htmlMatches })
    })
  return [...rows, ...results.filter(item => !isWarranty(item))]
}
